/**
 * Ticket Priority Indicator Component
 * Displays a colored chip or icon for ticket priority
 */

import { Chip, Box } from '@mui/material'
import { PriorityHigh, ArrowUpward, Remove } from '@mui/icons-material'
import { TicketPriority, TicketPriorityLabels } from '@api/types/tickets'

interface TicketPriorityIndicatorProps {
  priority: TicketPriority
  size?: 'small' | 'medium'
  variant?: 'chip' | 'icon'
}

const priorityColors: Record<TicketPriority, string> = {
  [TicketPriority.Low]: '#4caf50', // Green
  [TicketPriority.Medium]: '#ff9800', // Orange/Yellow
  [TicketPriority.High]: '#ff5722', // Deep Orange
  [TicketPriority.Critical]: '#f44336', // Red
}

const priorityIcons: Record<TicketPriority, React.ReactNode> = {
  [TicketPriority.Low]: <Remove fontSize="small" />,
  [TicketPriority.Medium]: <ArrowUpward fontSize="small" />,
  [TicketPriority.High]: <ArrowUpward fontSize="small" />,
  [TicketPriority.Critical]: <PriorityHigh fontSize="small" />,
}

export function TicketPriorityIndicator({
  priority,
  size = 'small',
  variant = 'chip',
}: TicketPriorityIndicatorProps) {
  const label = TicketPriorityLabels[priority]
  const color = priorityColors[priority]

  if (variant === 'icon') {
    return (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          color,
          fontWeight: priority === TicketPriority.Critical ? 'bold' : 'normal',
        }}
        aria-label={`Priority: ${label}`}
      >
        {priorityIcons[priority]}
        <span>{label}</span>
      </Box>
    )
  }

  return (
    <Chip
      label={label}
      size={size}
      icon={priority === TicketPriority.Critical ? <PriorityHigh /> : undefined}
      sx={{
        bgcolor: color,
        color: 'white',
        fontWeight: priority === TicketPriority.Critical ? 'bold' : 'normal',
        minWidth: 70,
        '& .MuiChip-icon': {
          color: 'white',
        },
      }}
      aria-label={`Priority: ${label}`}
    />
  )
}
